// apps/client/src/services/salaries.ts

import { ProjectSalarySummaryResponse } from '@avanzu/types';
import { apiClient } from './apiClient';

/**
 * Fetch the salary summary for a project — used by the transactions page to
 * determine whether the salary-loading toggle should be enabled. The toggle
 * is enabled when `projectAllocation > 0` (i.e. employees are allocated and
 * have payslip data in the project's tax-year window).
 */
export const fetchProjectSalarySummary = async (
  organisationId: string,
  projectId: string
): Promise<ProjectSalarySummaryResponse> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/projects/${projectId}/salaries/summary`
  );
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData?.error || 'Failed to load salary summary');
  }
  return (await response.json()) as ProjectSalarySummaryResponse;
};

/**
 * Freshness status of the project's salary-mix snapshot. Used by the Reports
 * page chip that surfaces when an async recompute has caught up. Cheap to
 * call — single-row read from `projectSalaryMix`.
 */
export interface ProjectSalaryMixStatus {
  success: boolean;
  hasMix: boolean;
  totalProjectAllocation?: number; // percent (0..100)
  computedAt?: string; // ISO timestamp
}

export const fetchProjectSalaryMixStatus = async (
  organisationId: string,
  projectId: string
): Promise<ProjectSalaryMixStatus> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/projects/${projectId}/salaries/mix-status`
  );
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData?.error || 'Failed to load salary mix status');
  }
  return (await response.json()) as ProjectSalaryMixStatus;
};
